/* eslint-disable react/require-default-props */
import React, { useCallback, useMemo, useRef } from 'react';

import { Circle, Circles, Container, Mensagem } from './styles';

export interface TokenInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  length?: number;
  error?: boolean;
  message?: React.ReactNode;
  messageIcon?: React.ReactNode;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

const apenasDigitos = (texto: string): string => texto.replace(/\D/g, '');

const TokenInput = ({
  value,
  onChange,
  onComplete,
  length = 6,
  error = false,
  message,
  messageIcon,
  disabled = false,
  'aria-label': ariaLabel = 'Código de verificação',
  className,
}: TokenInputProps): React.ReactElement => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const digitos = useMemo(
    () => Array.from({ length }, (_, i) => value[i] ?? ''),
    [value, length]
  );

  const aplicar = useCallback(
    (novo: string) => {
      const limpo = apenasDigitos(novo).slice(0, length);
      onChange(limpo);
      if (limpo.length === length) onComplete?.(limpo);
    },
    [length, onChange, onComplete]
  );

  const focar = (indice: number): void => {
    refs.current[indice]?.focus();
    refs.current[indice]?.select();
  };

  const handleChange = (
    indice: number,
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const digitado = apenasDigitos(e.target.value);
    if (!digitado) return;

    const atual = digitos.slice();
    digitado.split('').forEach((d, i) => {
      if (indice + i < length) atual[indice + i] = d;
    });
    aplicar(atual.join(''));

    const proximo = Math.min(indice + digitado.length, length - 1);
    focar(proximo);
  };

  const handleKeyDown = (
    indice: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const atual = digitos.slice();
      if (atual[indice]) {
        atual[indice] = '';
        aplicar(atual.join(''));
        return;
      }
      if (indice > 0) {
        atual[indice - 1] = '';
        aplicar(atual.join(''));
        focar(indice - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && indice > 0) {
      e.preventDefault();
      focar(indice - 1);
    }
    if (e.key === 'ArrowRight' && indice < length - 1) {
      e.preventDefault();
      focar(indice + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    const colado = apenasDigitos(e.clipboardData.getData('text'));
    if (!colado) return;
    e.preventDefault();
    aplicar(colado);
    focar(Math.min(colado.length, length - 1));
  };

  return (
    <Container className={className}>
      <Circles role="group" aria-label={ariaLabel}>
        {digitos.map((digito, indice) => (
          <Circle
            // eslint-disable-next-line react/no-array-index-key
            key={indice}
            ref={(el: HTMLInputElement | null) => {
              refs.current[indice] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={indice === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={digito}
            disabled={disabled}
            erro={error}
            preenchido={!!digito}
            desabilitado={disabled}
            aria-label={`Dígito ${indice + 1} de ${length}`}
            aria-invalid={error || undefined}
            onChange={(e) => handleChange(indice, e)}
            onKeyDown={(e) => handleKeyDown(indice, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </Circles>
      {message && (
        <Mensagem erro={error} role={error ? 'alert' : undefined}>
          {message}
          {messageIcon}
        </Mensagem>
      )}
    </Container>
  );
};

export default TokenInput;
